import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import { captureException } from '@sentry/browser';

import { TimelineEntry } from '../../../../types/__generated__';
import { useApp } from '../../../hooks';
import { useDeleteTimelineEntry } from '../../../hooks/timeline/useDeleteTimelineEntry';
import { useListTimelineEntries } from '../../../hooks/timeline/useListTimelineEntries';
import ConfirmationModal from '../../ConfirmationModal/ConfirmationModal';
import Modal from '../../Modal/Modal';

interface ConfirmationModalProps {
  timelineEntry: TimelineEntry;
  isConfirmationModalOpen: boolean;
  handleCloseConfirmationModal: () => void;
}

export const DeleteTimelineEntryConfirmationModal: FunctionComponent<ConfirmationModalProps> = ({
  timelineEntry,
  isConfirmationModalOpen,
  handleCloseConfirmationModal,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const deleteTimelineEntry = useDelete({ id: timelineEntry.id });

  if (!isConfirmationModalOpen) return null;

  return (
    <Modal
      onCloseRequest={handleCloseConfirmationModal}
      data-qa={'delete-confirmation-modal'}
    >
      <ConfirmationModal
        modalTitle={
          timelineEntry.fitnessActivity
            ? t('timeline.deleteTimelineEntry.modal.fitnessTitle')
            : t('timeline.deleteTimelineEntry.modal.nonFitnessTitle')
        }
        confirmButtonText={t('timeline.deleteTimelineEntry.modal.confirm')}
        dismissButtonText={t('timeline.deleteTimelineEntry.modal.dismiss')}
        onDismiss={handleCloseConfirmationModal}
        onConfirm={deleteTimelineEntry}
      >
        <p>
          {timelineEntry.fitnessActivity
            ? t('timeline.deleteTimelineEntry.modal.fitnessConfirmationMessage')
            : t(
                'timeline.deleteTimelineEntry.modal.nonFitnessConfirmationMessage',
              )}
        </p>
      </ConfirmationModal>
    </Modal>
  );
};

interface UseDelete {
  id: string;
}

function useDelete({ id }: UseDelete) {
  const { t } = useTranslation('common', { useSuspense: false });
  const { showToast } = useApp();
  const [deleteTimelineEntryMutation] = useDeleteTimelineEntry();
  const { data, fetchMore } = useListTimelineEntries();

  const pageInfo = data?.page?.timeline?.pageInfo;

  return async () => {
    try {
      await deleteTimelineEntryMutation(id);

      showToast({
        message: t('timeline.deleteTimelineEntry.confirmationMessage'),
        type: 'success',
        dismissAfter: 10000,
      });

      if (pageInfo?.endCursor && pageInfo.hasNextPage) {
        await fetchMore({ variables: { after: pageInfo.endCursor, first: 1 } });
      }
    } catch (err) {
      showToast({
        message: t('timeline.deleteTimelineEntry.errorMessage'),
        type: 'warning',
        dismissAfter: 10000,
      });

      captureException(err);
    }
  };
}
